import Component from '@glimmer/component';
import { service } from '@ember/service';
import { registerDestructor } from '@ember/destroyable';
import type CableService from 'tio-common/services/cable';
import type Owner from '@ember/owner';

type DocumentStateBroadcast = {
  transition?: { event?: string };
};

/**
 * DocumentActivityListener — renderless. While mounted, subscribes to the
 * signed-in person's ObservableDocumentStatusChannel stream (the server
 * defaults the stream to `current_user.person` when no person_id param is
 * sent, so no id crosses the wire) and calls @onActivity whenever a document
 * REFLECTION lands for them — regardless of who initiated it.
 *
 * This is the missing employee-side ear for admin-origin changes (see
 * workspace/docs/SLR_ADMIN_CHANGE_RESPONSIVENESS_AUDIT.md, gap 5): the
 * server already computes and broadcasts fresh slrAlerts on this stream
 * after every reflection — participant uploads, admin reflect/rerun, and
 * (since tio-api #10148) admin manual entries — but until now only
 * tio-admin listened. Only `content_reflected` triggers @onActivity: that
 * is the transition after which /slr-alerts can answer differently.
 * Intermediate extraction states are upload-progress noise here.
 *
 * The good-standing verdict is evaluated asynchronously AFTER reflection,
 * so a refresh fired from this event can render a pre-verdict state; the
 * caller's refresh recipe re-reads the verdict with reload:true, and the
 * next activity or visit converges. Demo mode needs no guard here — the
 * cable service is hard-disabled there and subscribe() no-ops.
 */
interface DocumentActivityListenerSignature {
  Args: {
    /** Fired on each reflection landed for the signed-in person. */
    onActivity: () => void;
  };
}

const CHANNEL_PARAMS = { channel: 'ObservableDocumentStatusChannel' };

export default class DocumentActivityListener extends Component<DocumentActivityListenerSignature> {
  @service declare cable: CableService;

  constructor(owner: Owner, args: DocumentActivityListenerSignature['Args']) {
    super(owner, args);

    this.cable.subscribe(CHANNEL_PARAMS, {
      received: (data: unknown) => {
        const message = data as DocumentStateBroadcast;
        if (message?.transition?.event === 'content_reflected') {
          this.args.onActivity();
        }
      },
    });

    registerDestructor(this, () => {
      this.cable.unsubscribe(CHANNEL_PARAMS);
    });
  }

  <template></template>
}
